import { Component } from "react"
import PropTypes from "prop-types"

import sluggify from "shared/lib/sluggify"
import { ingredientAdd, ingredientUpdate, ingredientDelete } from "./gql"

const FIELD_TYPES = [
  { value: "string", label: "String" },
  { value: "date", label: "Date (ISO8601)" },
  { value: "datetime", label: "Date with time (ISO8601)" },
  { value: "image_url", label: "Image URL" },
  { value: "file_url", label: "File URL" },
  { value: "web_url", label: "Web URL" },
]

class IngredientsForm extends Component {
  constructor(props) {
    super(props)
    const targetQuery = props.ingredient.target_query || {}
    this.state = {
      ingredient: {
        id: props.ingredient.id || "",
        name: props.ingredient.name || "",
        slug: props.ingredient.slug || "",
        note: props.ingredient.note || "",
        valueType: props.ingredient.value_type || "string",
        example: props.ingredient.example || "",
        targetQueryId: targetQuery.id,
      },
      queries: this.props.queries || [],
      submitting: false,
      errors: {},
    }
  }

  showFieldTypeUI = () => {
    switch (this.state.ingredient.valueType) {
      case "query":
        return (
          <div className="field">
            <div className="label">
              <label>Queries</label>
            </div>
            <div className="input">
              {this.state.queries.length > 0 ? (
                <select
                  name="targetQueryId"
                  value={this.state.ingredient.targetQueryId}
                  onChange={this.handleInputChange}
                >
                  {this.state.queries.map(q => (
                    <option key={q.id} value={q.id}>
                      {q.name}
                    </option>
                  ))}
                </select>
              ) : (
                <select disabled>
                  <option>No selectable query</option>
                </select>
              )}
            </div>
          </div>
        )
      case "string":
      case "image_url":
      case "file_url":
      case "web_url":
        return (
          <div className="field">
            <div className="label">
              <label>Example</label>
            </div>
            <div className="input">
              <input
                type="text"
                name="example"
                id="example"
                value={this.state.ingredient.example}
                onChange={this.handleInputChange}
                placeholder="E.g. https://yourwebpage.com/profile/album/1234.jpg"
                required
              />
            </div>
          </div>
        )
      default:
        return null
    }
  }

  handleInputChange = event => {
    const { name, value } = event.target
    this.updateIngredient({ [name]: value })
  }

  updateIngredient = attributes => {
    const ingredient = { ...this.state.ingredient, ...attributes }
    if (attributes.valueType) {
      if (["date", "datetime", "query"].includes(attributes.valueType)) {
        ingredient.example = ""
      }
      if (this.state.ingredient.valueType == "query") {
        ingredient.targetQueryId = null
      }
      if (attributes.valueType == "query" && this.state.queries.length > 0) {
        ingredient.targetQueryId = this.state.queries[0].id
      }
    }
    this.setState({ ingredient })
  }

  handleNameBlur = event => {
    const ingredient = this.state.ingredient
    if (ingredient.name !== undefined) {
      this.updateIngredient({ name: sluggify(ingredient.name, "Camel") })
    }
    if (ingredient.slug === "" && ingredient.name !== "") {
      this.updateIngredient({ slug: sluggify(ingredient.name) })
    }
  }

  handleErrors = errors => {
    const errorsToHash = errors.reduce((acc, cur) => {
      acc[cur.attribute] = `${cur.attribute} ${cur.message}`
      return acc
    }, {})
    if (errors && errors.length > 0) {
      this.setState({ errors: errorsToHash })
      throw errors
    } else {
      this.setState({ errors: {} })
    }
  }

  handleSubmit = event => {
    event.preventDefault()
    this.props.create ? this.handleCreate() : this.handleUpdate()
  }

  handleCreate = () => {
    this.setState({ submitting: true })
    return ingredientAdd(
      this.props.parent.id,
      this.props.parentType,
      this.state.ingredient
    )
      .then(({ ingredient, errors }) => {
        this.setState({ submitting: false })
        this.handleErrors(errors)
        // segment
        this.props.updateIngredients("add", ingredient)
        this.props.hideForm()
        window.appendFlash("Ingredient created")
      })
      .catch(errs => {
        window.appendFlash(
          "Could not create ingredient — please check your data",
          "danger"
        )
        this.setState({ submitting: false })
      })
  }

  handleUpdate = () => {
    this.setState({ submitting: true })
    return ingredientUpdate(this.state.ingredient)
      .then(({ ingredient, errors }) => {
        this.setState({ submitting: false })
        this.handleErrors(errors)
        // segment
        this.props.updateIngredients("update", ingredient)
        this.props.hideForm()
        window.appendFlash("Ingredient updated")
      })
      .catch(errs => {
        window.appendFlash(
          "Could not update ingredient — please check your data",
          "danger"
        )
        this.setState({ submitting: false })
      })
  }

  handleDelete = () => {
    this.setState({ submitting: true })
    return ingredientDelete(this.state.ingredient)
      .then(({ errors }) => {
        this.setState({ submitting: false })
        this.handleErrors(errors)
        // segment
        this.props.hideForm()
        this.props.updateIngredients("delete", this.state.ingredient)
        window.appendFlash("Ingredient deleted")
      })
      .catch(errs => {
        this.setState({ submitting: false })
        window.appendFlash(
          "Could not delete ingredient — please check your data",
          "danger"
        )
      })
  }

  handleCancelClick = event => {
    event.preventDefault()
    this.props.hideForm()
  }

  confirmDelete = event => {
    event.preventDefault()
    if (
      window.confirm(
        "Are you sure you want to PERMANENTLY delete this ingredient?"
      )
    ) {
      return this.handleDelete()
    }
  }

  render() {
    return (
      <>
        <form className="service-edit form" onSubmit={this.handleSubmit}>
          <section>
            <div className="list-item-form">
              <div className="field">
                <div className="label">
                  <label htmlFor="name">Name</label>
                </div>
                <div className="input">
                  <input
                    type="text"
                    name="name"
                    id="name"
                    value={this.state.ingredient.name}
                    onBlur={this.handleNameBlur}
                    onChange={this.handleInputChange}
                    required
                  />
                </div>
                <p className="note">In UpperCamelCase </p>
                {this.state.errors.name && (
                  <FormError errorText={this.state.errors.name} />
                )}
              </div>

              <div className="field">
                <div className="label">
                  <label htmlFor="slug">Slug</label>
                </div>
                <div className="input">
                  <input
                    type="text"
                    name="slug"
                    id="slug"
                    value={this.state.ingredient.slug}
                    minLength={4}
                    pattern="^[a-zA-Z0-9-_]+$"
                    onChange={this.handleInputChange}
                    required
                  />
                </div>
                <p className="note">In snake_case </p>
                {this.state.errors.slug && (
                  <FormError errorText={this.state.errors.slug} />
                )}
              </div>

              <div className="field">
                <div className="label">
                  <label htmlFor="note">Note</label>
                </div>
                <div className="input">
                  <input
                    type="text"
                    name="note"
                    id="note"
                    value={this.state.ingredient.note}
                    onChange={this.handleInputChange}
                    required
                  />
                </div>
                <p className="note">Must be a complete sentence</p>
                {this.state.errors.note && (
                  <FormError errorText={this.state.errors.note} />
                )}
              </div>

              <div className="field">
                <div className="label">
                  <label>Type</label>
                </div>
                <div className="input">
                  <select
                    name="valueType"
                    value={this.state.ingredient.valueType}
                    onChange={this.handleInputChange}
                  >
                    {FIELD_TYPES.map(i => (
                      <option key={i.value} value={i.value}>
                        {i.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {this.showFieldTypeUI()}

              <div className="actions">
                <div className="action-left">
                  {this.props.ingredient.id &&
                    !this.props.service.is_published && (
                      <a
                        href=""
                        className="secondary-action delete link-standalone"
                        onClick={this.confirmDelete}
                      >
                        Delete ingredient
                      </a>
                    )}
                </div>
                <div className="action-right">
                  <a
                    role="button"
                    className="button-secondary button-cancel-link"
                    onClick={this.handleCancelClick}
                  >
                    Cancel
                  </a>
                  <button
                    type="submit"
                    className="button-secondary"
                    disabled={
                      this.props.service.is_published || this.state.submitting
                    }
                  >
                    {this.state.submitting ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </section>
        </form>
      </>
    )
  }
}

const FormError = ({ errorText }) => (
  <span className="error-message">Error: {errorText}</span>
)

IngredientsForm.propTypes = {
  service: PropTypes.object.isRequired,
  updateIngredients: PropTypes.func.isRequired,
  hideForm: PropTypes.func.isRequired,
  parent: PropTypes.object.isRequired,
  parentType: PropTypes.string.isRequired,
  queries: PropTypes.array.isRequired,
  ingredient: PropTypes.object,
  create: PropTypes.bool,
}

IngredientsForm.defaultProps = {
  create: false,
  ingredient: {},
}

export default IngredientsForm
